import { useEffect, useState } from "react";
import { ArrowDown, MapPin, Sparkles, Medal, Landmark, Globe2, GraduationCap, Megaphone, Briefcase, Building2 } from "lucide-react";
import { profile } from "@/data/profile";

const roles = [
  "Curriculum Developer",
  "Legislative Intern",
  "Club Founder & President",
  "Model UN Delegate",
  "Tutor & Mentor",
  "Civic Advocate",
];

const highlights = [
  { icon: Medal, jsx: <>Presidential Volunteer Service Award <strong>Gold Medalist</strong></> },
  { icon: Landmark, jsx: <><strong>1,100+ immigrants</strong> taught financial literacy at Khalsa Financial Care</> },
  { icon: Building2, jsx: <><strong>PA House Legislative Intern</strong>, knocked on 500+ doors and ran community job fairs</> },
  { icon: Briefcase, jsx: <><strong>BlackRock Extern</strong>, selected by the COO</> },
  { icon: Megaphone, jsx: <>Founder &amp; President of Active Impact Club, <strong>200+ members strong</strong></> },
  { icon: Globe2, jsx: <>Represented <strong>3 nations</strong> in Model UN committees</> },
  { icon: GraduationCap, jsx: <><strong>Distinguished Honor Roll</strong> in every marking period since 7th grade</> },
];

const glance = [
  { area: "Finance", role: "Research & Curriculum Developer", org: "Khalsa Financial Care", impact: "1,100+ immigrants taught through 30+ free financial workshops" },
  { area: "Finance", role: "Extern, COO Selected", org: "BlackRock, NYC", impact: "Learned how the world's largest asset manager operates" },
  { area: "Government", role: "Legislative Intern", org: "PA House · Rep. Brian Munroe", impact: "Knocked on 500+ constituent doors to hear local issues and connect residents with state services" },
  { area: "Government", role: "Event Lead, Job Fairs", org: "PA House · Rep. Brian Munroe", impact: "Ran and managed the office's employment event, recruiting local businesses and bringing residents in to meet them" },
  { area: "Education", role: "Tutor", org: "Kumon North America", impact: "200+ students taught in Math & English" },
  { area: "Education", role: "Assistant Teacher, Punjabi", org: "Sikh Sabha of New Jersey", impact: "Taught Punjabi reading and writing at Sunday school, Oct 2024 to Mar 2026" },
  { area: "Leadership", role: "Founder & President", org: "Active Impact Club", impact: "Founded and leads one of the school's biggest clubs with 200+ members" },
  { area: "Service", role: "Gold Medalist", org: "Presidential Volunteer Service Award", impact: "Highest national tier for volunteer service" },
  { area: "Diplomacy", role: "Delegate", org: "Model United Nations", impact: "Represented 3 nations in 3 committees" },
  { area: "Academics", role: "4.25 Weighted GPA · Distinguished Honor Roll", org: "Council Rock HS South", impact: "Every marking period since 7th grade" },
];

export const Hero = () => {
  const [roleIdx, setRoleIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx((i) => (i + 1) % roles.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="hero section" id="hero">
      <div className="container">
        <div className="hero-grid">
          <div className="hero-content">
            <p className="eyebrow reveal" data-testid="hero-eyebrow">
              <Sparkles size={13} /> Portfolio · Class of 2027
            </p>
            <h1 className="hero-name reveal delay-1" data-testid="hero-name-headline">
              Arjan <em>Bajwa</em>
            </h1>
            <p className="hero-title reveal delay-2" data-testid="hero-subheading-title">
              Student Leader · <span key={roleIdx} className="role-rotate" data-testid="hero-rotating-role">{roles[roleIdx]}</span>
            </p>
            <p className="hero-sub reveal delay-3">
              Building financial confidence for immigrant communities, teaching the next
              generation, and learning how policy shapes lives, all before graduation.
            </p>
            <ul className="hero-highlights reveal delay-3" data-testid="hero-highlights">
              {highlights.map(({ icon: Icon, jsx }, i) => (
                <li key={i}>
                  <span className="highlight-icon"><Icon size={14} /></span>
                  <span>{jsx}</span>
                </li>
              ))}
            </ul>
            <div className="hero-meta reveal delay-4">
              <span className="meta-chip" data-testid="hero-location">
                <MapPin size={13} /> {profile.location}
              </span>
              <span className="meta-chip">Council Rock High School South</span>
            </div>
            <button
              className="hero-cta reveal delay-5"
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-scroll-cta"
            >
              Explore my journey <ArrowDown size={15} />
            </button>
          </div>
          <div className="hero-photo-wrap reveal delay-3">
            <div className="hero-photo" data-testid="hero-profile-photo">
              <img src="/images/arjan-headshot.jpg" alt="Arjan Bajwa" />
            </div>
          </div>
        </div>
        <div className="hero-stats reveal delay-4" data-testid="hero-stats">
          <div className="stat">
            <span className="stat-num">1,100+</span>
            <span className="stat-label">Immigrants taught financial literacy</span>
          </div>
          <div className="stat">
            <span className="stat-num">200+</span>
            <span className="stat-label">Students tutored</span>
          </div>
          <div className="stat">
            <span className="stat-num">500+</span>
            <span className="stat-label">Constituent doors knocked</span>
          </div>
          <div className="stat">
            <span className="stat-num">Gold</span>
            <span className="stat-label">Presidential Service Award</span>
          </div>
        </div>
        <div className="glance reveal delay-5" data-testid="hero-glance-table">
          <table>
            <thead>
              <tr>
                <th>Area</th>
                <th>Role / Honor</th>
                <th>Organization</th>
                <th>Impact</th>
              </tr>
            </thead>
            <tbody>
              {glance.map((g) => (
                <tr key={g.role}>
                  <td><span className="glance-area">{g.area}</span></td>
                  <td className="glance-role">{g.role}</td>
                  <td className="glance-org">{g.org}</td>
                  <td className="glance-impact">{g.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
